import type {
  CSSProperties,
  FocusEvent,
  FormEvent,
  KeyboardEvent,
  RefObject,
} from 'react';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  buildSmartyAutocompleteSearch,
  createSmartyUsAutocompleteLookup,
  getSmartyUsAutocompleteClient,
  RegistrationAddressT,
  SmartyUsAutocompleteSuggestionT,
} from '@consumer-frontend/nc-utils';

export type UseSmartyAutocompleteParamsT = {
  address: RegistrationAddressT;
  onAddressChange: (address: RegistrationAddressT) => void;
};

export type UseSmartyAutocompleteResultT = {
  formContainerRef: RefObject<HTMLDivElement | null>;
  getSuggestionId: (index: number) => string;
  highlightedSuggestionIndex: number;
  isLoadingSuggestions: boolean;
  onAddressFieldBlur: (event: FocusEvent<HTMLElement>) => void;
  onAddressFieldChange: (event: FormEvent<HTMLElement>) => void;
  onAddressFieldFocus: (event: FocusEvent<HTMLElement>) => void;
  onAddressFieldKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
  onSelectSuggestion: (suggestion: SmartyUsAutocompleteSuggestionT) => void;
  showSuggestions: boolean;
  suggestions: SmartyUsAutocompleteSuggestionT[];
  suggestionsRef: RefObject<HTMLDivElement | null>;
  suggestionsStyle: CSSProperties;
  errorMessage?: string;
};

const DEFAULT_SUGGESTION_HEIGHT = 256;

const getSuggestionsTop = ({
  availableSpaceAbove,
  availableSpaceBelow,
  fieldBottom,
  fieldTop,
  formTop,
  suggestionHeight,
}: {
  availableSpaceAbove: number;
  availableSpaceBelow: number;
  fieldBottom: number;
  fieldTop: number;
  formTop: number;
  suggestionHeight: number;
}): number => {
  const shouldPlaceAbove = suggestionHeight > availableSpaceBelow && availableSpaceAbove > availableSpaceBelow;

  return shouldPlaceAbove
    ? fieldTop - formTop - suggestionHeight - 4
    : fieldBottom - formTop + 4;
};

/** Keeps Smarty request state separate from the RHF-owned address values. */
export const useSmartyAutocomplete = ({
  address,
  onAddressChange,
}: UseSmartyAutocompleteParamsT): UseSmartyAutocompleteResultT => {
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [hasFocus, setHasFocus] = useState<boolean>(false);
  const [highlightedSuggestionIndex, setHighlightedSuggestionIndex] = useState<number>(-1);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState<boolean>(false);
  const [isSuggestionSelectionCommitted, setIsSuggestionSelectionCommitted] = useState<boolean>(false);
  const [suggestions, setSuggestions] = useState<SmartyUsAutocompleteSuggestionT[]>([]);
  const [suggestionsStyle, setSuggestionsStyle] = useState<CSSProperties>({});
  const [activeAddressField, setActiveAddressField] = useState<HTMLElement | null>(null);
  const autocompleteClient = useMemo(
    () => getSmartyUsAutocompleteClient(import.meta.env.VITE_SMARTY_EMBEDDED_KEY),
    [],
  );
  const formContainerRef = useRef<HTMLDivElement>(null);
  const addressFieldRef = useRef<HTMLElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const addressRef = useRef(address);

  addressRef.current = address;

  const clearSuggestions = useCallback((): void => {
    setHighlightedSuggestionIndex(-1);
    setIsLoadingSuggestions(false);
    setSuggestions([]);
  }, []);

  const onSelectSuggestion = useCallback(
    (suggestion: SmartyUsAutocompleteSuggestionT): void => {
      onAddressChange({
        addressLine1: suggestion.streetLine,
        addressLine2: suggestion.secondary || address.addressLine2,
        city: suggestion.city,
        postalCode: suggestion.zipcode,
        state: suggestion.state as RegistrationAddressT['state'],
      });
      clearSuggestions();
      setErrorMessage(undefined);
      setHasFocus(true);
      setIsSuggestionSelectionCommitted(true);
    },
    [
      address.addressLine2,
      clearSuggestions,
      onAddressChange,
    ],
  );

  const onAddressFieldKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>): void => {
      const highlightedSuggestion = highlightedSuggestionIndex >= 0
        ? suggestions[highlightedSuggestionIndex]
        : undefined;

      if (event.key === 'ArrowDown' && suggestions.length) {
        event.preventDefault();
        setHighlightedSuggestionIndex((currentIndex) => (
          currentIndex < suggestions.length - 1 ? currentIndex + 1 : 0
        ));

        return;
      }

      if (event.key === 'ArrowUp' && suggestions.length) {
        event.preventDefault();
        setHighlightedSuggestionIndex((currentIndex) => (
          currentIndex > 0 ? currentIndex - 1 : suggestions.length - 1
        ));

        return;
      }

      if (event.key === 'Enter' && highlightedSuggestion) {
        event.preventDefault();
        onSelectSuggestion(highlightedSuggestion);

        return;
      }

      if (event.key === 'Escape') {
        clearSuggestions();
      }
    },
    [
      clearSuggestions,
      highlightedSuggestionIndex,
      onSelectSuggestion,
      suggestions,
    ],
  );

  const onAddressFieldBlur = useCallback((event: FocusEvent<HTMLElement>): void => {
    window.requestAnimationFrame(() => {
      const fieldName = event.target instanceof HTMLInputElement
        ? event.target.name
        : '';

      if (fieldName.endsWith('addressLine1')) {
        setHasFocus(false);
        setActiveAddressField(null);
        clearSuggestions();
      }
    });
  }, [clearSuggestions]);

  const onAddressFieldChange = useCallback((event: FormEvent<HTMLElement>): void => {
    const fieldName = event.target instanceof HTMLInputElement
      ? event.target.name
      : '';

    if (fieldName.endsWith('addressLine1')) {
      setIsSuggestionSelectionCommitted(false);
    }
  }, []);

  const onAddressFieldFocus = (event: FocusEvent<HTMLElement>): void => {
    const fieldName = event.target instanceof HTMLInputElement
      ? event.target.name
      : '';

    if (fieldName.endsWith('addressLine1')) {
      if (event.target instanceof HTMLElement) {
        addressFieldRef.current = event.target;
        setActiveAddressField(event.target);
      }

      setHasFocus(true);
      setIsSuggestionSelectionCommitted(false);
    }
  };

  const updateSuggestionPosition = useCallback((): void => {
    const formElement = formContainerRef.current;
    const fieldElement = activeAddressField || addressFieldRef.current;

    if (!formElement || !fieldElement || !suggestions.length) {
      setSuggestionsStyle({});

      return;
    }

    const formRect = formElement.getBoundingClientRect();
    const fieldRect = fieldElement.getBoundingClientRect();
    const suggestionHeight = suggestionsRef.current?.getBoundingClientRect().height || DEFAULT_SUGGESTION_HEIGHT;
    const top = getSuggestionsTop({
      availableSpaceAbove: fieldRect.top,
      availableSpaceBelow: window.innerHeight - fieldRect.bottom,
      fieldBottom: fieldRect.bottom,
      fieldTop: fieldRect.top,
      formTop: formRect.top,
      suggestionHeight,
    });

    setSuggestionsStyle({
      left: fieldRect.left - formRect.left,
      top: Math.max(top, 0),
      width: fieldRect.width,
    });
  }, [activeAddressField, suggestions.length]);

  /** Fetch suggestions after the user pauses while editing the street address. */
  useEffect(
    () => {
      const currentAddress = addressRef.current;
      const street = currentAddress.addressLine1.trim();
      const search = buildSmartyAutocompleteSearch({
        street: currentAddress.addressLine1,
      });
      let isCancelled = false;

      if (isSuggestionSelectionCommitted || !autocompleteClient || street.length < 3 || !search || !hasFocus) {
        clearSuggestions();

        return undefined;
      }

      const timeoutId = window.setTimeout(
        async () => {
          setIsLoadingSuggestions(true);

          try {
            const lookup = createSmartyUsAutocompleteLookup(search);
            const response = await autocompleteClient.send(lookup);

            if (!isCancelled) {
              setErrorMessage(undefined);
              setHighlightedSuggestionIndex(response.result.length ? 0 : -1);
              setSuggestions(response.result);
            }
          } catch (error) {
            if (!isCancelled) {
              setErrorMessage(error instanceof Error ? error.message : 'Smarty autocomplete failed.');
              setSuggestions([]);
            }
          } finally {
            if (!isCancelled) {
              setIsLoadingSuggestions(false);
            }
          }
        },
        300,
      );

      return () => {
        isCancelled = true;
        window.clearTimeout(timeoutId);
      };
    },
    [
      address.addressLine1,
      autocompleteClient,
      clearSuggestions,
      hasFocus,
      isSuggestionSelectionCommitted,
    ],
  );

  /** Recalculate the dropdown when its field or viewport changes. */
  useEffect(
    () => {
      updateSuggestionPosition();
      window.addEventListener('resize', updateSuggestionPosition);
      window.addEventListener('scroll', updateSuggestionPosition, true);

      return () => {
        window.removeEventListener('resize', updateSuggestionPosition);
        window.removeEventListener('scroll', updateSuggestionPosition, true);
      };
    },
    [updateSuggestionPosition],
  );

  /** Keep the keyboard-highlighted option visible. */
  useEffect(
    () => {
      if (highlightedSuggestionIndex >= 0) {
        document.getElementById(`address-suggestion-${highlightedSuggestionIndex}`)?.scrollIntoView({ block: 'nearest' });
      }
    },
    [highlightedSuggestionIndex],
  );

  return {
    errorMessage,
    formContainerRef,
    getSuggestionId: (index: number): string => `address-suggestion-${index}`,
    highlightedSuggestionIndex,
    isLoadingSuggestions,
    onAddressFieldBlur,
    onAddressFieldChange,
    onAddressFieldFocus,
    onAddressFieldKeyDown,
    onSelectSuggestion,
    showSuggestions: hasFocus && !!suggestions.length,
    suggestions,
    suggestionsRef,
    suggestionsStyle,
  };
};
